import { Reference, relayStylePagination } from '@apollo/client/utilities';
import { getCurrentToken } from '@teamexos/fit-shared';
import type { ClientOptions } from '@teamexos/prince-sdk';

import fragments from '../graphql/fragments.json';

const mergeNodes = (existing: Reference[], incoming: Reference[]) => {
  if (!existing) {
    return incoming;
  }

  if (!incoming) {
    return existing;
  }

  const existingRefs = existing.map(({ __ref }) => __ref);

  // dedupe any incoming and preserve order
  const incomingDeduped = incoming.filter(
    ({ __ref }) => !existingRefs.includes(__ref),
  );

  return [...existing, ...incomingDeduped];
};

const mergeEdges = (
  existing: Array<{ node: Reference }>,
  incoming: Array<{ node: Reference }>,
) => {
  if (!existing) {
    return incoming;
  }

  if (!incoming) {
    return existing;
  }

  const existingRefs = existing.map(({ node: { __ref } }) => __ref);

  // dedupe any incoming chats and preserve order
  const incomingDeduped = incoming.filter(
    ({ node: { __ref } }) => !existingRefs.includes(__ref),
  );

  return [...existing, ...incomingDeduped];
};

export const clientOptions: ClientOptions = {
  getToken: async () => {
    const tokens = await getCurrentToken();
    return tokens?.idToken?.toString();
  },
  clientUrl:
    process.env.GATSBY_API_URL || 'https://graphql.dev.prince.ex-os.net/',
  apolloClientOptions: {
    clientAwareness: {
      name: 'shared-platform-admin',
      version: process.env.GATSBY_COMMIT_ID ?? 'unknown',
    },
    devtools: { enabled: process.env.NODE_ENV === 'development' },
  },
  possibleTypes: fragments.possibleTypes,
  typePolicies: {
    Query: {
      fields: {
        usePush: {
          read: (existing: boolean | undefined) =>
            typeof existing === 'boolean' ? existing : true,
        },
        auditLogs: relayStylePagination(['filter']),
      },
    },
    User: {
      fields: {
        chatRooms: relayStylePagination(['filters', 'orderBy']),
        auditLogs: relayStylePagination(),
        activity: relayStylePagination(['filters']),
        currentPractice: {
          merge: true,
        },
      },
    },
    Client: {
      fields: {
        coaches: relayStylePagination(),
        // DomainConnection has no ID, so without merging, a query that only
        // asks for the count drops the nodes another query cached.
        domains: { merge: true },
      },
    },
    CoachLocation: {
      fields: {
        coaches: relayStylePagination(),
      },
    },
    CoachData: {
      fields: {
        // Key only on `filter` — NOT on `input`, which carries the pagination
        // cursor (`after`). Keying on `input` stored every page under a separate
        // cache entry, so the active query always read page 1's `pageInfo` and
        // re-sent the same cursor. relayStylePagination can't be used here because
        // it expects a top-level `after`/`before` arg, but the cursor is nested in
        // `input`; the CoachUsersConnection.edges merge below handles accumulation.
        coachUsers: {
          keyArgs: ['filter', 'orderBy'],
        },
      },
    },
    ActivityConnection: {
      fields: {
        nodes: {
          merge: (existing, incoming, options) => {
            // we only want to merge nodes if is pagination
            // otherwise we want the incoming, because filters may change
            if (!options?.variables?.input?.after) {
              return incoming;
            }

            return mergeNodes(existing, incoming);
          },
        },
      },
    },
    UserChatRoomsConnection: {
      fields: {
        // Required for the original member list (still used by Perform)
        nodes: {
          merge: mergeNodes,
        },
        // Required for member list v1 used by fit
        edges: {
          merge: mergeEdges,
        },
      },
    },
    ChatRoom: {
      fields: {
        messages: relayStylePagination(),
      },
    },
    ClientUsersConnection: {
      fields: {
        edges: {
          merge: mergeEdges,
        },
      },
    },
    AuditLogsConnection: {
      fields: {
        edges: {
          merge: mergeEdges,
        },
      },
    },
    LocationCoachesConnection: {
      fields: {
        edges: {
          merge: mergeEdges,
        },
      },
    },
    CoachUsersConnection: {
      fields: {
        edges: {
          merge: (existing, incoming, options) => {
            // Only accumulate edges when paginating (fetchMore sets input.after).
            // On a fresh fetch/refetch the list may have shrunk, so replace it —
            // otherwise stale edges from a previous result linger in the cache.
            if (!options?.variables?.input?.after) {
              return incoming;
            }

            return mergeEdges(existing, incoming);
          },
        },
      },
    },
    ChatRoomMessagesConnection: {
      fields: {
        nodes: {
          merge(existing: Reference[], incoming: Reference[], { readField }) {
            const nodes: Record<string, Reference> = {};

            (existing ?? []).forEach((r) => {
              const key = readField('id', r);
              if (typeof key === 'string' || typeof key === 'number') {
                nodes[key] = r;
              }
            });
            (incoming ?? []).forEach((r) => {
              const key = readField('id', r);
              if (typeof key === 'string') {
                nodes[key] = r;
              }
            });

            // Sort messages by id
            return Object.values(nodes).sort(
              (a, b) =>
                Number(readField('id', a) ?? '') -
                (Number(readField('id', b)) ?? ''),
            );
          },
        },
      },
    },
  },
  debug: process.env.NODE_ENV === 'development',
};
